<template>
  <div class="page-shell" :class="{ 'page-shell--products': isProductLayout }">
    <ScrollProgress />
    <AnimatedBackground />
    <header
      class="site-header"
      :class="{ 'site-header--scrolled': headerScrolled }"
    >
      <div class="header-inner">
        <a href="#" class="logo-wrap" @click.prevent="goHome">
          <span class="logo-mark logo-mark--header"></span>
          <div class="logo-text">
            <span class="logo-main">{{ company.brand }}</span>
            <span class="logo-sub">{{ company.shortName }}</span>
          </div>
        </a>
        <button
          type="button"
          class="mobile-menu-btn"
          :aria-expanded="mobileMenuOpen"
          aria-label="菜单"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <span></span><span></span><span></span>
        </button>
        <nav class="main-nav" :class="{ 'main-nav--open': mobileMenuOpen }">
          <div
            v-for="item in mainNavItems"
            :key="item.label"
            class="nav-dropdown"
          >
            <a
              href="#"
              class="nav-dropdown__trigger"
              :class="{ 'nav-dropdown__trigger--active': isMainNavActive(item.label) }"
              @click.prevent="onMainNavClick(item.label)"
            >{{ item.label }}</a>
            <div class="nav-dropdown__panel">
              <div
                v-for="child in item.children"
                :key="child.label"
                class="nav-dropdown__group"
                :class="{ 'nav-dropdown__group--has-sub': child.children?.length }"
              >
                <a
                  v-if="!child.children?.length"
                  href="#"
                  class="nav-dropdown__item"
                  @click.prevent="onNavItemClick(child.label)"
                >
                  <svg
                    class="nav-dropdown__icon"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path :d="navIcons[child.icon]" />
                  </svg>
                  <span class="nav-dropdown__label">{{ child.label }}</span>
                  <span v-if="child.hasSub" class="nav-dropdown__arrow">›</span>
                </a>
                <template v-else>
                  <div class="nav-dropdown__item nav-dropdown__item--parent">
                    <svg
                      class="nav-dropdown__icon"
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path :d="navIcons[child.icon]" />
                    </svg>
                    <span class="nav-dropdown__label">{{ child.label }}</span>
                    <span class="nav-dropdown__arrow">›</span>
                  </div>
                  <div class="nav-dropdown__subpanel">
                    <a
                      v-for="sub in child.children"
                      :key="sub.label"
                      href="#"
                      class="nav-dropdown__subitem"
                      @click.prevent="onNavItemClick(sub.label)"
                    >
                      {{ sub.label }}
                    </a>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </nav>
        <div class="header-actions">
          <div
            v-for="item in utilNavItems"
            :key="item.label"
            class="nav-dropdown"
          >
            <a href="#" class="nav-util nav-dropdown__trigger" :class="{ 'nav-dropdown__trigger--active': route.name === 'about' }" @click.prevent="goAbout">{{ item.label }}</a>
            <div class="nav-dropdown__panel nav-dropdown__panel--util">
              <a
                v-for="child in item.children"
                :key="child.label"
                href="#"
                class="nav-dropdown__item"
                @click.prevent="onUtilNavClick(child.label)"
              >
                <svg
                  class="nav-dropdown__icon"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path :d="navIcons[child.icon]" />
                </svg>
                <span class="nav-dropdown__label">{{ child.label }}</span>
              </a>
            </div>
          </div>
          <div class="nav-dropdown nav-dropdown--lang">
            <button type="button" class="lang-switch nav-dropdown__trigger" aria-label="切换语言">
              <svg class="lang-icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              <span>CH</span>
            </button>
            <div class="nav-dropdown__panel nav-dropdown__panel--lang">
              <a
                v-for="lang in langOptions"
                :key="lang"
                href="#"
                class="nav-dropdown__item nav-dropdown__item--lang"
              >
                {{ lang }}
              </a>
            </div>
          </div>
          <button type="button" class="search-btn" aria-label="搜索" @click="searchOpen = true">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </button>
          <button type="button" class="btn-contact" @click="goConsult">获取方案</button>
        </div>
      </div>
    </header>

    <main class="page-content">
      <router-view v-slot="{ Component }">
        <transition name="page-fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <section class="contact-strip">
      <div class="container contact-strip-inner">
        <p class="strip-slogan">{{ company.slogan }}</p>
        <p class="strip-contact">
          <span>手机：{{ company.phone }}</span>
          <span class="strip-sep">|</span>
          <span>地址：{{ company.address }}</span>
        </p>
      </div>
    </section>

    <footer class="site-footer">
      <div class="container footer-main">
        <div class="footer-brand-box">
          <a href="#" class="footer-brand-logo">
            <span class="logo-mark logo-mark--sm"></span>
            <span>天津高仁科技</span>
          </a>
        </div>
        <div class="footer-col">
          <h4>产品中心</h4>
          <a href="#" @click.prevent="goProducts('software')">系统类</a>
          <a href="#" @click.prevent="goProducts('smart-line')">教育类</a>
          <a href="#" @click.prevent="goProducts('humanoid')">机器人类</a>
          <a href="#" @click.prevent="goProducts('agv')">AGV类</a>
          <a href="#" @click.prevent="goProducts('automation-line')">非标自动化类</a>
        </div>
        <div class="footer-col">
          <h4>解决方案</h4>
          <a href="#" @click.prevent="goSolutions('cases')">行业案例</a>
          <a href="#" @click.prevent="goSolutions('dev')">机器人开发方案</a>
        </div>
        <div class="footer-col">
          <h4>服务与支持</h4>
          <a href="#" @click.prevent="goDownloads">下载中心</a>
          <a href="#" @click.prevent="goTraining">培训</a>
          <a href="#" @click.prevent="goConsult">联系我们</a>
          <a href="#" @click.prevent="goAbout">关于我们</a>
        </div>
        <div class="footer-col">
          <h4>关于我们</h4>
          <a href="#" @click.prevent="goAbout">公司简介</a>
          <a href="#" @click.prevent="goNews">新闻资讯</a>
          <a href="#" @click.prevent="goConsult">商务合作</a>
        </div>
        <div class="footer-col footer-col--social">
          <h4>关注我们</h4>
          <div class="social-icons">
            <a href="#" class="social-icon social-icon--wechat" title="微信">微</a>
            <a href="#" class="social-icon social-icon--weibo" title="微博">博</a>
            <a href="#" class="social-icon social-icon--video" title="视频号">视</a>
          </div>
        </div>
      </div>
      <div class="footer-bottom">
        <div class="container footer-bottom-inner">
          <p class="copyright">
            Copyright © 2026 {{ company.name }}
            <a
              class="icp"
              :href="company.icpUrl"
              target="_blank"
              rel="noopener noreferrer"
            >{{ company.icp }}</a>
          </p>
          <div class="footer-legal">
            <a href="#">隐私政策</a>
            <a href="#">服务条款</a>
          </div>
        </div>
      </div>
    </footer>

    <SearchOverlay v-model:open="searchOpen" />
    <BackToTop />

    <a href="#" class="float-chat" title="在线咨询" @click.prevent="goConsult">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
        <path
          d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.2L4 17.2V4h16v12z"
        />
      </svg>
    </a>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { defaultCategoryId, navLabelToCategoryId } from './data/productCatalog'
import { solutionNavLabels } from './data/solutionsCatalog'
import { company } from './data/siteConfig'
import AnimatedBackground from './components/AnimatedBackground.vue'
import ScrollProgress from './components/ScrollProgress.vue'
import SearchOverlay from './components/SearchOverlay.vue'
import BackToTop from './components/BackToTop.vue'

const route = useRoute()
const router = useRouter()
const headerScrolled = ref(false)
const mobileMenuOpen = ref(false)
const searchOpen = ref(false)
const isProductLayout = computed(() => route.name !== 'home')

function isMainNavActive(label) {
  const name = route.name
  if (label === '产品中心') return name === 'products' || name === 'product-detail'
  if (label === '解决方案') return name === 'solutions'
  if (label === '下载中心') return name === 'downloads'
  if (label === '新闻动态') return name === 'news'
  if (label === '培训') {
    return [
      'training',
      'training-schedule',
      'training-course',
      'training-mine',
      'training-exam',
      'training-knowledge',
    ].includes(name)
  }
  if (label === '服务与支持') {
    return name === 'home' && route.query.section === 'consult'
  }
  return false
}

watch(() => route.fullPath, () => {
  mobileMenuOpen.value = false
})

function onScroll() {
  headerScrolled.value = window.scrollY > 20
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

function goHome() {
  router.push({ name: 'home' })
}

function goProducts(categoryId) {
  router.push({ name: 'products', params: { categoryId } })
}

function goSolutions(tab = 'cases') {
  router.push({ name: 'solutions', params: { tab } })
}

function goAbout() {
  router.push({ name: 'about' })
}

function goDownloads() {
  router.push({ name: 'downloads' })
}

function goNews(category) {
  router.push({ name: 'news', params: category ? { category } : {} })
}

function goConsult() {
  router.push({ name: 'home', query: { section: 'consult' } })
}

function goTraining() {
  router.push({ name: 'training' })
}

function onMainNavClick(label) {
  if (label === '产品中心') goProducts(defaultCategoryId)
  else if (label === '解决方案') goSolutions('cases')
  else if (label === '下载中心') goDownloads()
  else if (label === '新闻动态') goNews()
  else if (label === '培训') goTraining()
  else if (label === '服务与支持') goConsult()
}

function onUtilNavClick(label) {
  if (label === '公司简介' || label === '加入我们') goAbout()
  else if (label === '联系我们') goConsult()
}

function onNavItemClick(label) {
  const solutionTab = solutionNavLabels[label]
  if (solutionTab) {
    goSolutions(solutionTab)
    return
  }
  if (label === '产品资料') {
    goDownloads()
    return
  }
  if (label === '产品视频') {
    goNews('video')
    return
  }
  if (label === '公司新闻') {
    goNews('company')
    return
  }
  if (label === '公司视频') {
    goNews('video')
    return
  }
  if (label === '帮助中心' || label === '技术支持') {
    goConsult()
    return
  }
  if (label === '高仁学堂' || label === '加工培训' || label === '工业视觉培训') {
    goTraining()
    return
  }
  if (label === '学习中心') {
    router.push({ name: 'training-mine' })
    return
  }
  if (label === '在线考试') {
    router.push({ name: 'training-exam' })
    return
  }
  if (label === '知识资料') {
    router.push({ name: 'training-knowledge' })
    return
  }
  if (label === '培训安排') {
    router.push({ name: 'training-schedule' })
    return
  }
  if (label === '我的学习') {
    router.push({ name: 'training-mine' })
    return
  }
  const categoryId = navLabelToCategoryId[label]
  if (categoryId) {
    goProducts(categoryId)
  }
}

const mainNavItems = [
  {
    label: '产品中心',
    children: [
      {
        label: '系统类',
        icon: 'robot',
        children: [
          { label: '工业软件系统定制' },
          { label: '电能风能与新能源系统' },
          { label: '工业互联网平台' },
        ],
      },
      {
        label: '教育类',
        icon: 'building',
        children: [
          { label: '智能智造产线应用综合实训平台' },
          { label: '工业机器人技能实训装置（埃斯顿）' },
          { label: '智能制造自动化生产线综合实训系统' },
          { label: '工业机器人系统运维实训平台' },
          { label: '典型传感器技术实验箱' },
          { label: '工业传感器综合实验装置' },
          { label: '高级电工实训考核装置' },
          { label: '电工电子技术综合实验装置' },
          { label: '生产系统网络与通讯运维实践平台' },
          { label: '太阳能光伏发电实验装置' },
          { label: '可编程控制器实训装置' },
          { label: '机械装调技能实训考核装置' },
          { label: '焊接机器人工作站' },
          { label: '数字资源定制开发' },
          { label: '数字教材定制开发出版' },
          { label: '专业教学资源库开发' },
          { label: '技能大赛技术支持与专家指导' },
          { label: '高校及职业院校科研技术合作' },
        ],
      },
      {
        label: '机器人类',
        icon: 'robot',
        children: [
          { label: '人型机器人' },
          { label: '运维机器狗' },
          { label: '人工智能技术定制开发' },
          { label: '无人机产品定制开发' },
        ],
      },
      {
        label: 'AGV类',
        icon: 'parts',
        children: [
          { label: 'AGV产品定制开发' },
          { label: '移动机器人产品定制开发' },
        ],
      },
      {
        label: '非标自动化类',
        icon: 'blueprint',
        children: [
          { label: '自动化产线非标定制' },
          { label: '原创工业级产品' },
          { label: '案例效果图' },
          { label: '主要服务客户' },
        ],
      },
    ],
  },
  {
    label: '解决方案',
    children: [
      { label: '行业案例', icon: 'building' },
      { label: '机器人开发方案', icon: 'blueprint' },
    ],
  },
  {
    label: '下载中心',
    children: [
      { label: '产品资料', icon: 'download' },
      { label: '产品视频', icon: 'video' },
    ],
  },
  {
    label: '服务与支持',
    children: [
      { label: '帮助中心', icon: 'faq' },
      { label: '技术支持', icon: 'support' },
    ],
  },
  {
    label: '新闻动态',
    children: [
      { label: '公司新闻', icon: 'news' },
      { label: '公司视频', icon: 'video' },
    ],
  },
  {
    label: '培训',
    children: [
      { label: '高仁学堂', icon: 'course' },
      { label: '我的学习', icon: 'course' },
      { label: '在线考试', icon: 'course' },
      { label: '知识资料', icon: 'course' },
    ],
  },
]

const utilNavItems = [
  {
    label: '关于我们',
    children: [
      { label: '公司简介', icon: 'about' },
      { label: '加入我们', icon: 'join' },
      { label: '联系我们', icon: 'contact' },
    ],
  },
]

const langOptions = [
  'English',
  '한국어',
  '日本語',
  'Deutsch',
  'Italiano',
  'Français',
  'Svenska',
  'Español',
  'Português',
]

const navIcons = {
  chip: 'M6 4h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2zm2 4v8h8V8H8zm2 2h4v4h-4v-4z',
  app: 'M4 4h16v16H4V4zm2 2v12h12V6H6zm2 2h8v2H8V8zm0 4h5v2H8v-2z',
  robot: 'M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h3a3 3 0 013 3v1h1a2 2 0 012 2v5a2 2 0 01-2 2h-1v1a2 2 0 01-2 2h-2v2h-2v-2H9a2 2 0 01-2-2v-1H6a2 2 0 01-2-2v-5a2 2 0 012-2h1v-1a3 3 0 013-3h3V5.73A2 2 0 0112 2zm-1 8H9a1 1 0 00-1 1v5h12v-5a1 1 0 00-1-1h-2v2h-2v-2h-4v2h-2v-2z',
  parts: 'M12 2l2.4 4.8L20 8l-3.6 3.5.9 5.5L12 15l-5.3 2 .9-5.5L4 8l5.6-1.2L12 2z',
  building: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z',
  blueprint: 'M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 3h7v4h-7v-4zm0-6h7v4h-7V10z',
  download: 'M5 20h14v-2H5v2zm7-18l-5.5 5.5 1.42 1.42L11 6.17V16h2V6.17l3.08 3.09 1.42-1.42L12 2z',
  video: 'M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z',
  faq: 'M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2a2 2 0 114 0c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z',
  support: 'M12 1a9 9 0 00-9 9v4c0 1.66 1.34 3 3 3h1v-6H5v-1a7 7 0 0114 0v1h-2v6h1c1.66 0 3-1.34 3-3v-4a9 9 0 00-9-9z',
  course: 'M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z',
  cert: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  about: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z',
  join: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  contact: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z',
  news: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z',
}
</script>

<style scoped>
.page-shell {
  font-family: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
  color: var(--text-primary);
  background: transparent;
  min-width: 0;
}

.page-shell--products {
  min-width: 0;
}

.page-content {
  position: relative;
  z-index: 1;
}

a {
  text-decoration: none;
  color: inherit;
}

.site-header {
  height: 72px;
  background: rgba(255, 255, 255, 0.88);
  border-bottom: 1px solid var(--border-soft);
  position: sticky;
  top: 0;
  z-index: 200;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.04);
  transition:
    box-shadow 0.35s var(--ease-smooth),
    background 0.35s var(--ease-smooth);
}

.site-header--scrolled {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.header-inner {
  height: 100%;
  max-width: 1920px;
  margin: 0 auto;
  padding: 0 120px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  position: relative;
}

.logo-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.logo-mark--header {
  width: 40px;
  height: 40px;
  background: var(--gradient-brand);
  border-radius: 10px;
  position: relative;
  flex-shrink: 0;
}

.logo-mark--header::after {
  content: 'G';
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  font-weight: 800;
}

.logo-main {
  font-size: 28px;
  font-weight: 700;
  color: var(--brand-primary);
  letter-spacing: 1px;
}

.logo-sub {
  margin-top: 3px;
  font-size: 13px;
  font-weight: 500;
  color: var(--brand-primary);
  letter-spacing: 0.5px;
}

.main-nav {
  display: flex;
  align-items: center;
  margin-left: 60px;
  gap: 36px;
  flex-shrink: 0;
}

.nav-dropdown {
  position: relative;
}

.nav-dropdown__trigger {
  display: inline-flex;
  align-items: center;
  position: relative;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  transition: color 0.25s var(--ease-smooth);
  cursor: pointer;
  background: none;
  border: none;
  font-family: inherit;
  padding: 4px 0;
}

.nav-dropdown__trigger::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 0;
  height: 2px;
  background: var(--gradient-brand);
  border-radius: var(--radius-pill);
  transition: width 0.35s var(--ease-out-expo);
}

.nav-dropdown:hover .nav-dropdown__trigger::after,
.nav-dropdown__trigger:hover::after,
.nav-dropdown__trigger--active::after {
  width: 100%;
}

.nav-dropdown:hover .nav-dropdown__trigger,
.nav-dropdown__trigger:hover,
.nav-dropdown__trigger--active {
  color: var(--brand-primary);
}

.nav-dropdown__panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 220px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-md);
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition:
    opacity 0.28s var(--ease-smooth),
    transform 0.28s var(--ease-out-expo),
    visibility 0.28s;
  z-index: 300;
}

.nav-dropdown__panel::before {
  content: '';
  position: absolute;
  top: -10px;
  left: 0;
  right: 0;
  height: 10px;
}

.nav-dropdown:hover .nav-dropdown__panel {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.nav-dropdown__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  border-radius: var(--radius-sm);
  transition: background 0.2s, color 0.2s, padding-left 0.25s var(--ease-smooth);
  white-space: nowrap;
}

.nav-dropdown__item:hover {
  background: rgba(23, 107, 137, 0.06);
  color: var(--brand-primary);
  padding-left: 22px;
}

.nav-dropdown__icon {
  flex-shrink: 0;
  color: #555;
}

.nav-dropdown__label {
  flex: 1;
}

.nav-dropdown__arrow {
  flex-shrink: 0;
  font-size: 16px;
  color: #888;
  line-height: 1;
}

.nav-dropdown__group {
  position: relative;
}

.nav-dropdown__group--has-sub:hover > .nav-dropdown__item--parent {
  background: rgba(0, 0, 0, 0.05);
  color: #1a1a1a;
}

.nav-dropdown__item--parent {
  cursor: default;
  user-select: none;
}

.nav-dropdown__subpanel {
  position: absolute;
  left: calc(100% - 4px);
  top: 0;
  min-width: 240px;
  max-width: 320px;
  padding: 6px 0;
  background: rgba(245, 247, 250, 0.98);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.04);
  opacity: 0;
  visibility: hidden;
  transform: translateX(6px);
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
  z-index: 310;
}

.nav-dropdown__group--has-sub:hover .nav-dropdown__subpanel {
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
}

.nav-dropdown__subpanel::before {
  content: '';
  position: absolute;
  top: 0;
  left: -8px;
  width: 8px;
  height: 100%;
}

.nav-dropdown__subitem {
  display: block;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  line-height: 1.45;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}

.nav-dropdown__subitem:hover {
  background: rgba(23, 107, 137, 0.06);
  color: var(--brand-primary);
}

.nav-dropdown--lang .nav-dropdown__panel {
  right: 0;
  left: auto;
  min-width: 148px;
  padding: 4px 0;
}

.nav-dropdown__item--lang {
  padding: 9px 20px;
  font-size: 13px;
  font-weight: 400;
  color: #444;
}

.nav-dropdown__panel--util {
  min-width: 168px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 22px;
  margin-left: auto;
  flex-shrink: 0;
}

.nav-util {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
}

.nav-util:hover {
  color: var(--brand-primary);
}

.lang-switch {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px 0 8px;
  background: var(--gradient-brand);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}

.nav-dropdown--lang .lang-switch.nav-dropdown__trigger {
  background: var(--gradient-brand);
  color: #fff;
}

.nav-dropdown--lang:hover .lang-switch.nav-dropdown__trigger {
  color: #fff;
}

.lang-icon {
  flex-shrink: 0;
}

.search-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(23, 107, 137, 0.08);
  border: none;
  border-radius: 50%;
  color: var(--brand-primary);
  cursor: pointer;
  transition: background 0.2s;
}

.search-btn:hover {
  background: rgba(23, 107, 137, 0.14);
}

.btn-contact {
  height: 34px;
  padding: 0 22px;
  background: var(--gradient-brand);
  color: #fff;
  border: none;
  border-radius: 17px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: var(--shadow-brand);
  transition:
    transform 0.3s var(--ease-smooth),
    box-shadow 0.3s var(--ease-smooth);
}

.btn-contact:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(23, 107, 137, 0.28);
}

.logo-mark {
  width: 28px;
  height: 28px;
  background: var(--gradient-brand);
  border-radius: 6px;
  flex-shrink: 0;
}

.logo-mark--sm {
  width: 28px;
  height: 28px;
}


.contact-strip {
  position: relative;
  z-index: 1;
  padding: 56px 0;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 248, 252, 0.96) 100%);
  border-top: 1px solid var(--border-soft);
  overflow: hidden;
}

.contact-strip::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: min(680px, 80%);
  height: 1px;
  background: var(--gradient-brand);
  opacity: 0.35;
}

.strip-slogan {
  margin: 0 0 14px;
  font-size: clamp(22px, 3vw, 28px);
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: 0.5px;
}

.strip-contact {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.8;
}

.strip-sep {
  margin: 0 16px;
  color: #ccc;
}

.contact-strip-inner {
  text-align: center;
}

.site-footer {
  position: relative;
  z-index: 1;
  background: var(--gradient-footer);
  color: #94a3b8;
  padding-top: 56px;
}

.site-footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--gradient-brand);
  opacity: 0.85;
}

.footer-main {
  display: grid;
  grid-template-columns: 200px 1fr 1fr 1fr 140px;
  gap: 40px;
  padding-bottom: 36px;
  align-items: start;
}

.footer-brand-box {
  padding-top: 4px;
}

.footer-brand-logo {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  background: rgba(255, 255, 255, 0.04);
}

.footer-col h4 {
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}

.footer-col a {
  display: block;
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 10px;
}

.footer-col a:hover {
  color: #e5e7eb;
}

.social-icons {
  display: flex;
  gap: 10px;
}

.social-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  transition: transform 0.25s var(--ease-smooth), box-shadow 0.25s;
}

.social-icon:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.social-icon--wechat {
  background: #07c160;
}

.social-icon--weibo {
  background: #e6162d;
}

.social-icon--video {
  background: #1a6dff;
}

.footer-bottom {
  border-top: 1px solid #333;
  padding: 16px 0;
}

.footer-bottom-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.copyright {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.icp {
  margin-left: 12px;
  color: inherit;
  text-decoration: none;
}

.icp:hover {
  color: #9ca3af;
  text-decoration: underline;
}

.footer-legal {
  display: flex;
  gap: 24px;
}

.footer-legal a {
  font-size: 12px;
  color: #6b7280;
}

.float-chat {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 52px;
  height: 52px;
  background: #07c160;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 20px rgba(7, 193, 96, 0.4);
  z-index: 300;
  transition: transform 0.3s var(--ease-smooth), box-shadow 0.3s;
  animation: pulseRing 2.5s ease-out infinite;
}

.float-chat:hover {
  transform: scale(1.08);
  box-shadow: 0 8px 28px rgba(7, 193, 96, 0.5);
}

@media (max-width: 1400px) {
  .header-inner {
    padding: 0 48px;
  }
  .main-nav {
    margin-left: 40px;
    gap: 28px;
  }
  .nav-dropdown__trigger,
  .nav-util {
    font-size: 14px;
  }
}

.mobile-menu-btn {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  margin-left: auto;
}

.mobile-menu-btn span {
  display: block;
  height: 2px;
  background: #333;
  border-radius: 2px;
  transition: transform 0.3s;
}

@media (max-width: 1100px) {
  .mobile-menu-btn {
    display: flex;
  }
  .main-nav {
    display: none;
    position: absolute;
    top: 72px;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    margin: 0;
    padding: 12px 16px 20px;
    background: rgba(255, 255, 255, 0.98);
    border-bottom: 1px solid #eee;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    max-height: calc(100vh - 72px);
    overflow-y: auto;
  }
  .main-nav--open {
    display: flex;
  }
  .nav-dropdown__panel {
    position: static;
    opacity: 1;
    visibility: visible;
    transform: none;
    box-shadow: none;
    border: none;
    padding: 0 0 0 16px;
    display: none;
  }
  .nav-dropdown:hover .nav-dropdown__panel,
  .nav-dropdown:focus-within .nav-dropdown__panel {
    display: block;
  }
  .nav-dropdown__subpanel {
    position: static;
    opacity: 1;
    visibility: visible;
    transform: none;
    box-shadow: none;
    padding-left: 16px;
    display: none;
  }
  .nav-dropdown__group--has-sub:hover .nav-dropdown__subpanel,
  .nav-dropdown__group--has-sub:focus-within .nav-dropdown__subpanel {
    display: block;
  }
  .header-actions .nav-dropdown__panel {
    display: none;
  }
  .header-actions .nav-dropdown:hover .nav-dropdown__panel,
  .header-actions .nav-dropdown:focus-within .nav-dropdown__panel {
    display: block;
  }
}

@media (max-width: 1240px) {
  .page-shell {
    min-width: auto;
  }
  .header-inner {
    padding: 0 16px;
    flex-wrap: wrap;
    height: auto;
    min-height: 72px;
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .main-nav {
    margin-left: 0;
    order: 3;
    width: 100%;
    flex-wrap: wrap;
    gap: 16px 24px;
    padding-top: 8px;
  }
  .header-actions {
    gap: 10px;
  }
  .btn-contact {
    display: none;
  }
  .footer-main {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
}

@media (max-width: 640px) {
  .header-actions .nav-dropdown:not(.nav-dropdown--lang) {
    display: none;
  }
  .footer-main {
    grid-template-columns: 1fr;
  }
  .footer-bottom-inner {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
}
</style>
